import type { CrudFilter, CrudOperators } from '@refinedev/core';

type LogicalCrudFilter = Extract<CrudFilter, { field: string }>;
const isLogical = (f: CrudFilter): f is LogicalCrudFilter => 'field' in f;

/** The subset of Refine's `setFilters` the filter dropdowns rely on. */
export type SetFilters = (filters: CrudFilter[], mode: 'merge') => void;

const DEFAULT_OPERATORS: CrudOperators[] = [
  'eq',
  'in',
  'nin',
  'gte',
  'lte',
  // Exclusive upper bound of a day range — on its own when only "to" is set.
  'lt',
  'null',
  'nnull',
];

/**
 * Whether `field` currently has an active filter, for a column's `filtered` prop.
 *
 * Never use antd's `filteredValue` for this: a column counts as controlled as
 * soon as the prop exists, and one controlled column makes antd discard the
 * internal filter state of every uncontrolled column on each render.
 */
export const isFieldFiltered = (
  filters: CrudFilter[] | undefined,
  field: string,
  operators: CrudOperators[] = DEFAULT_OPERATORS
): boolean =>
  (filters ?? []).some(
    (f) =>
      isLogical(f) &&
      f.field === field &&
      operators.includes(f.operator) &&
      f.value != null
  );

/** Current value of the `field`/`operator` filter, for seeding dropdown state. */
export const getFilterValue = <T = unknown>(
  filters: CrudFilter[] | undefined,
  field: string,
  operator: CrudOperators
): T | undefined => {
  const filter = (filters ?? []).find(
    (f) => isLogical(f) && f.field === field && f.operator === operator
  );
  return filter?.value ?? undefined;
};

export const isDateFiltered = (filters: CrudFilter[], field: string): boolean =>
  isFieldFiltered(filters, field, ['gte', 'lte']);
